import { Link } from "react-router-dom";
import { industries } from "@/mocks/homeData";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function IndustriesSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: btnRef, isVisible: btnVisible } = useScrollReveal();

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
            Industries We Serve
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight">
            Solutions Tailored to <strong>Every Sector</strong>
          </h2>
          <p className="text-gray-500 text-sm mt-4 max-w-lg mx-auto leading-relaxed">
            From law enforcement to data centers, our product portfolio spans the full spectrum of industries that demand reliable, high-performance technology.
          </p>
        </div>

        {/* Industry Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((ind, i) => (
            <Link
              key={ind.title}
              to="/industries"
              className={`group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-500 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{
                transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
                height: "320px",
              }}
            >
              {/* Background image */}
              <div className="absolute inset-0">
                <img
                  src={ind.image}
                  alt={ind.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </div>

              {/* Gradient overlay — default */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/90 via-[#1a1a1a]/40 to-[#1a1a1a]/10 transition-opacity duration-500" />

              {/* Hover dark overlay */}
              <div className="absolute inset-0 bg-[#1a1a1a]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Left accent bar */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#e63b2e] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out origin-bottom" />

              {/* Default state content — bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-6 transition-all duration-400 ease-out group-hover:opacity-0 group-hover:translate-y-2">
                {/* Icon pill */}
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 mb-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${ind.icon} text-white text-xs`} />
                  </div>
                  <span className="text-white text-[11px] font-semibold tracking-wide">{ind.title}</span>
                </div>
                <p className="text-white/70 text-xs leading-relaxed line-clamp-2">{ind.description}</p>
              </div>

              {/* Hover overlay caption — slides up */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out">
                {/* Red category label */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[#e63b2e] text-[10px] font-bold uppercase tracking-widest">Industry</span>
                  <div className="flex-1 h-px bg-[#e63b2e]/40" />
                </div>

                {/* Title */}
                <h3 className="text-white text-lg font-bold mb-2 leading-tight">{ind.title}</h3>

                {/* Divider */}
                <div className="w-8 h-px bg-white/30 mb-3" />

                {/* Description */}
                <p className="text-white/75 text-sm leading-relaxed mb-4">{ind.description}</p>

                {/* Icon badge */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/15 backdrop-blur-sm rounded-lg">
                    <i className={`${ind.icon} text-white text-sm`} />
                  </div>
                </div>

                {/* CTA */}
                <div className="flex items-center gap-2 group/cta cursor-pointer">
                  <span className="text-white text-xs font-semibold">Explore Industry</span>
                  <div className="w-6 h-6 flex items-center justify-center bg-[#e63b2e] rounded-full transition-transform duration-300 group-hover/cta:translate-x-1">
                    <i className="ri-arrow-right-line text-white text-xs" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div
          ref={btnRef}
          className={`text-center mt-10 transition-all duration-700 ease-out ${btnVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <Link
            to="/industries"
            className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-medium px-7 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
          >
            Explore Industries
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
